/**
 * v3SessionBridge — cross-origin session handshake with the V3 shell app.
 *
 * V3 is the sole auth owner in the iframe architecture. This app runs at a
 * different origin (different port) and has its own isolated localStorage, so
 * it cannot access V3's session directly.
 *
 * This bridge applies for EVERY user type (standard login AND vendor SSO):
 *
 * Initial handshake (once per sessionStorage lifetime):
 * 1. V2 sends V2_REQUEST_SESSION to parent on boot.
 * 2. V3 responds with V3_VENDOR_SESSION (accessToken, loginUserId, loginUserOrgId).
 * 3. V2 writes credentials to localStorage, sets isLoggedIn cookie, removes
 *    stale OIDC user, then reloads once.
 * 4. After reload, V3_SESSION_APPLIED_KEY in sessionStorage prevents re-apply.
 *
 * Token refresh (ongoing):
 * - The message listener stays active in embedded mode.
 * - When V3 refreshes tokens it pushes an updated V3_VENDOR_SESSION message.
 * - V2 updates localStorage in place (no reload).
 */

import Cookies from 'universal-cookie';
import { isLoggedKey } from '../util/constants';
import { logger } from '../util/logger';
import { EMBED_SESSION_APPLIED_KEY } from './embedMode';
import { userManager } from './userManager';

const V3_SESSION_APPLIED_KEY = EMBED_SESSION_APPLIED_KEY;

export const V3_SESSION_UPDATED_EVENT = 'v3-session-updated';

type V3VendorSessionMessage = {
  type: 'V3_VENDOR_SESSION';
  accessToken: string;
  loginUserId: string;
  loginUserOrgId: string;
  refreshToken?: string;
  /** JSON-stringified user profile sent from V3 localStorage. */
  userData?: string;
  /**
   * 'ready' means V3 completed the full auth flow (Keycloak → OTP → Terms)
   * before mounting the iframe.  V2 can safely skip its own auth initiation.
   */
  authPhase?: 'ready';
};

function isV3VendorSessionMessage(data: unknown): data is V3VendorSessionMessage {
  if (!data || typeof data !== 'object') return false;
  const message = data as Record<string, unknown>;
  return (
    message['type'] === 'V3_VENDOR_SESSION' &&
    typeof message['accessToken'] === 'string' &&
    typeof message['loginUserId'] === 'string' &&
    typeof message['loginUserOrgId'] === 'string'
  );
}

/**
 * Sets the `isLoggedIn` cookie so that V2's `authService.isAuthenticated()`
 * returns `true` after the bridge-triggered page reload.
 */
function setIsLoggedInCookie(): void {
  const cookies = new Cookies();
  cookies.set(isLoggedKey, 'true', {
    path: '/',
    sameSite: 'lax',
    secure: globalThis.location.protocol === 'https:',
  });
}

function writeSessionCredentials(message: V3VendorSessionMessage): void {
  localStorage.setItem('access_token', message.accessToken);
  localStorage.setItem('loginUserId', message.loginUserId);
  localStorage.setItem('loginUserOrgId', message.loginUserOrgId);

  if (message.refreshToken) {
    localStorage.setItem('refresh_token', message.refreshToken);
  }

  if (message.userData) {
    localStorage.setItem('userData', message.userData);
  }
}

function applyV3Session(message: V3VendorSessionMessage): void {
  const alreadyApplied = sessionStorage.getItem(V3_SESSION_APPLIED_KEY) === 'true';

  if (alreadyApplied) {
    logger.log('[v3SessionBridge] Updating V3 session credentials in place');
    writeSessionCredentials(message);
    globalThis.dispatchEvent(new CustomEvent(V3_SESSION_UPDATED_EVENT));
    return;
  }

  const hadSameToken =
    localStorage.getItem('access_token') === message.accessToken;

  logger.log('[v3SessionBridge] Applying initial V3 session credentials');
  writeSessionCredentials(message);

  if (message.authPhase === 'ready') {
    setIsLoggedInCookie();
  }

  void userManager.removeUser();

  sessionStorage.setItem(V3_SESSION_APPLIED_KEY, 'true');

  // Same token already present — reload would only cancel in-flight XHRs.
  if (hadSameToken && message.authPhase === 'ready') {
    globalThis.dispatchEvent(new CustomEvent(V3_SESSION_UPDATED_EVENT));
    return;
  }

  globalThis.location.reload();
}

const v3ShellOrigins = (process.env.REACT_APP_V3_SHELL_FRAME_ANCESTORS ?? '')
  .split(/[\s,]+/)
  .filter(Boolean);

function isTrustedOrigin(origin: string): boolean {
  if (v3ShellOrigins.length === 0) return true;
  return v3ShellOrigins.includes(origin);
}

function getParentTargetOrigin(): string {
  const ancestorOrigin = globalThis.location.ancestorOrigins?.item(0);
  if (
    ancestorOrigin &&
    (v3ShellOrigins.length === 0 || v3ShellOrigins.includes(ancestorOrigin))
  ) {
    return ancestorOrigin;
  }

  if (v3ShellOrigins.length === 1) {
    return v3ShellOrigins[0];
  }

  return '*';
}

function handleV3SessionMessage(event: MessageEvent): void {
  if (!isTrustedOrigin(event.origin)) return;

  if (isV3VendorSessionMessage(event.data)) {
    applyV3Session(event.data);
  }
}

/** Ask the V3 parent shell to push the latest session credentials. */
export function requestV3SessionFromParent(): void {
  if (globalThis.self === globalThis.top) {
    return;
  }

  globalThis.parent.postMessage(
    { type: 'V2_REQUEST_SESSION' },
    getParentTargetOrigin()
  );
}

/**
 * Tell V3 the iframe session is dead (refresh 401). V3 owns Keycloak / T&C
 * and must open the dashboard modal or parent login — not V2 pages in the iframe.
 */
export function notifyParentSessionExpired(): void {
  if (globalThis.self === globalThis.top) {
    return;
  }

  globalThis.parent.postMessage(
    { type: 'V2_SESSION_EXPIRED' },
    getParentTargetOrigin()
  );
}

/**
 * Hand the expired session to V3 and never settle. Callers must not reject a
 * 401 after this — that would flash the iframe error page / CRA overlay while
 * the parent navigates to login or the T&C modal.
 */
export function handOffExpiredSessionToParent(): Promise<never> {
  notifyParentSessionExpired();
  return new Promise<never>(() => {
    /* stop here and let the parent app take over */
  });
}

/**
 * Waits briefly for an in-place session update from V3 (after a 401 refresh).
 * Resolves true when credentials were updated, false on timeout.
 */
export function waitForV3SessionUpdate(timeoutMs = 400): Promise<boolean> {
  if (globalThis.self === globalThis.top) {
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    const onUpdate = (): void => {
      clearTimeout(timer);
      resolve(true);
    };

    const timer = setTimeout(() => {
      globalThis.removeEventListener(V3_SESSION_UPDATED_EVENT, onUpdate);
      resolve(false);
    }, timeoutMs);

    globalThis.addEventListener(V3_SESSION_UPDATED_EVENT, onUpdate, {
      once: true,
    });
  });
}

/**
 * Boots the v3 session bridge. Call once before React renders so the listener
 * is registered before any authenticated API calls fire.
 */
export function initV3SessionBridge(): void {
  if (globalThis.self === globalThis.top) {
    return;
  }

  globalThis.addEventListener('message', handleV3SessionMessage);

  if (sessionStorage.getItem(V3_SESSION_APPLIED_KEY) === 'true') {
    logger.log(
      '[v3SessionBridge] Session already applied — listening for token updates'
    );
    return;
  }

  requestV3SessionFromParent();
  logger.log('[v3SessionBridge] Sent V2_REQUEST_SESSION to parent');
}
